import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { CheckLocationExceptionOutDTO, GlobalDayWithInterval, GlobalTimeslots, OpenHourExceptionTypeEnum } from '@api/ringil-api.v2';
import dayjs from 'dayjs';
import { formatDateFromStringWithLocale, stringYMDFromDayjs } from '@utils/dateWithTz';
import AlertInfo from '@components/obsolete/Alert/AlertInfo.tsx';
import { isNotNullOrUndefined } from '@utils/utils.tsx';

interface ComponentProps {
  customerLocationId: string;
  slots: GlobalTimeslots;
}

const CompanyExceptionWarning: React.FC<ComponentProps> = ({ customerLocationId, slots }) => {
  const { tr } = useTranslationLgs();
  const { checkLocationException } = useContext(ApiContext);
  const [warning, setWarning] = useState<CheckLocationExceptionOutDTO[]>();

  useEffect(() => {
    if (customerLocationId && slots && slots?.dayWithInterval && slots?.dayWithInterval?.length > 0) {
      const toLocalDate = (slot: GlobalDayWithInterval): string =>
        isNotNullOrUndefined(slot.utcDay) ? slot.utcDay : stringYMDFromDayjs(dayjs(slot.day));

      const firstDay = toLocalDate(slots.dayWithInterval[0]);
      const lastDay = toLocalDate(slots.dayWithInterval[slots.dayWithInterval.length - 1]);

      checkLocationException(customerLocationId, firstDay, lastDay, data => {
        setWarning(data);
      });
    }
  }, [slots, customerLocationId]);

  // dates[] on CheckLocationExceptionOutDTO are plain local date strings (YYYY-MM-DD)
  const printInterval = (dates: string[]) => {
    if (dates.length == 0) return 'nikdy';
    if (dates.length == 1) return formatDateFromStringWithLocale(dates[0]);
    return `${formatDateFromStringWithLocale(dates[0])} - ${formatDateFromStringWithLocale(dates[dates.length - 1])}`;
  };

  // ExceptionTimeIntervalDTO: localStart/localEnd are plain time strings in location TZ (preferred);
  // since/till are deprecated but same format — atomic check
  const printTimeInterval = (item: { since: string; till: string; localStart?: string; localEnd?: string }) => {
    const since = isNotNullOrUndefined(item.localStart) ? item.localStart : item.since;
    const till = isNotNullOrUndefined(item.localEnd) ? item.localEnd : item.till;
    return `${since} - ${till}`;
  };

  return (
    <>
       {warning && warning.length > 0 && (
         <AlertInfo backgroundWrapper={'orange2'}>
           {warning.map((w, index) => (
             <div key={index}>
               {w.openHourExceptionType == OpenHourExceptionTypeEnum.CLOSED && (
                 <>
                   {tr(`SupplierNotificationHeaderForm.exWarClosedWithTz`, `WARNING: closed on {{-dates}} {{exceptionName}}`, {
                     dates: printInterval(w.dates),
                     exceptionName: w.exceptionName,
                   })}
                 </>
               )}
               {w.openHourExceptionType == OpenHourExceptionTypeEnum.OPEN && (
                 <>
                   {tr(`SupplierNotificationHeaderForm.exWarOpenOnlyWithTz`, `WARNING: on {{-dates}} it is open in {{intervals}} {{exceptionName}}`, {
                     dates: printInterval(w.dates),
                     exceptionName: w.exceptionName,
                     intervals: w.openingHours.map(item => printTimeInterval(item)).join(', '),
                   })}
                 </>
               )}
             </div>
           ))}
         </AlertInfo>
       )}
    </>
  );
};

export default CompanyExceptionWarning;
